import React, { useState, useCallback, useEffect } from 'react';
import ReactModal from 'react-modal';
import { useNavigate } from 'react-router-dom';
import { useAuth, useTrip, app } from '@trip-planner-monorepo/shared';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { FaUserPlus, FaTrash, FaExternalLinkAlt, FaTimes, FaUsers, FaUserMinus } from 'react-icons/fa';
import './TripsList.css'; // Reusing styles or create new one

// Modal Styles
const modalStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    width: '90%',
    maxWidth: '600px',
    maxHeight: '90vh',
    overflowY: 'auto',
    padding: '24px',
    borderRadius: '12px',
    border: 'none',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
    backgroundColor: '#fff' // Or use CSS variable if available
  },
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 1000
  }
};

const TripManagementModal = ({ isOpen, onRequestClose, trip, onDelete }) => {
  const navigate = useNavigate();
  const { selectTrip } = useTrip();
  const { currentUser } = useAuth();

  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('viewer');
  const [isInviting, setIsInviting] = useState(false);
  const [inviteMessage, setInviteMessage] = useState('');
  const [inviteError, setInviteError] = useState('');

  const [showSharedUsers, setShowSharedUsers] = useState(false);
  const [sharedUsers, setSharedUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [usersError, setUsersError] = useState('');
  const [removingUid, setRemovingUid] = useState(null);

  // Reset shared users state when trip changes
  useEffect(() => {
    setSharedUsers([]);
    setShowSharedUsers(false);
    setUsersError('');
  }, [trip?.id]);

  const fetchSharedUsers = useCallback(async () => {
    if (!trip) return;
    const tripOwnerId = trip.isShared ? (trip.sharedBy || trip.createdBy) : (trip.createdBy || currentUser?.uid);
    setLoadingUsers(true);
    setUsersError('');
    try {
      const functions = getFunctions(app);
      const getSharedUsersFn = httpsCallable(functions, 'getSharedUsers');
      const result = await getSharedUsersFn({
        tripId: trip.id,
        tripOwnerId: tripOwnerId
      });
      setSharedUsers(result.data.users || []);
    } catch (error) {
      console.error('Error fetching shared users:', error);
      setUsersError('Failed to load shared users.');
    } finally {
      setLoadingUsers(false);
    }
  }, [trip, currentUser]);

  const handleRemoveUser = async (targetUid, email) => {
    if (!window.confirm(`Remove ${email} from this trip?`)) return;
    setRemovingUid(targetUid);
    try {
      const functions = getFunctions(app);
      const removeSharedUserFn = httpsCallable(functions, 'removeSharedUser');
      await removeSharedUserFn({
        tripId: trip.id,
        tripOwnerId: tripOwnerId,
        targetUid
      });
      setSharedUsers(prev => prev.filter(u => u.uid !== targetUid));
    } catch (error) {
      console.error('Error removing user:', error);
      setUsersError(error.message || 'Failed to remove user.');
    } finally {
      setRemovingUid(null);
    }
  };

  const handleToggleSharedUsers = () => {
    const next = !showSharedUsers;
    setShowSharedUsers(next);
    if (next && sharedUsers.length === 0) {
      fetchSharedUsers();
    }
  };

  if (!trip) return null;

  // For shared trips, the owner UID comes from the pointer (sharedBy), not necessarily createdBy
  const tripOwnerId = trip.isShared ? (trip.sharedBy || trip.createdBy) : (trip.createdBy || currentUser.uid);
  const isOwner = tripOwnerId === currentUser.uid;
  const isEditor = trip.sharedRole === 'editor' || (trip.roles && trip.roles[currentUser.uid] === 'editor');
  const canShare = isOwner || isEditor;

  const handleEnterTrip = () => {
    selectTrip(trip.id, trip.isShared ? trip.sharedBy : null);
    navigate(`/trips/${trip.id}`);
    onRequestClose();
  };

  const handleInviteUser = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;

    setIsInviting(true);
    setInviteMessage('');
    setInviteError('');

    try {
      const functions = getFunctions(app);
      const inviteUserToTrip = httpsCallable(functions, 'inviteUserToTrip');

      await inviteUserToTrip({
        email: inviteEmail,
        tripId: trip.id,
        tripOwnerId: tripOwnerId, // Crucial for finding the trip doc
        role: inviteRole
      });

      setInviteMessage(`Trip shared with ${inviteEmail}. They'll see it when they next log in.`);
      setInviteEmail('');
      // Refresh shared users list if visible
      if (showSharedUsers) {
        fetchSharedUsers();
      } else {
        setSharedUsers([]); // Reset so next toggle re-fetches
      }
    } catch (error) {
      console.error('Error inviting user:', error);
      setInviteError(error.message || 'Failed to invite user.');
    } finally {
      setIsInviting(false);
    }
  };

  return (
    <ReactModal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      style={modalStyles}
      contentLabel="Trip Management"
    >
      <div className="trip-modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ margin: 0 }}>{trip.name}</h2>
        <button onClick={onRequestClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2rem' }}>
          <FaTimes />
        </button>
      </div>

      <div className="trip-modal-content">
        <div className="trip-info-section" style={{ marginBottom: '24px' }}>
          <p>
            <strong>Dates: </strong>
            {trip.startDate ? (
              `${new Date(trip.startDate).toLocaleDateString()} - ${trip.endDate ? new Date(trip.endDate).toLocaleDateString() : '?'}`
            ) : 'No dates set'}
          </p>
          {trip.isShared && (
            <p>
              <strong>Shared by: </strong> {(() => {
                const name = trip.sharedByDisplayName || trip.sharedBy || 'Unknown';
                // Don't display raw Firebase UIDs - show friendly label instead
                return (name.length >= 20 && !name.includes('@') && !name.includes(' '))
                  ? 'Trip Owner'
                  : name;
              })()} (You are a {trip.sharedRole})
            </p>
          )}
        </div>

        <div className="trip-actions-primary" style={{ marginBottom: '32px' }}>
          <button
            onClick={handleEnterTrip}
            className="enter-trip-button"
            style={{
              width: '100%',
              padding: '12px',
              backgroundColor: '#4CAF50',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '1.1rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
          >
            <FaExternalLinkAlt /> Enter Trip Planner
          </button>
        </div>

        {canShare && (
          <div className="sharing-section" style={{ borderTop: '1px solid #eee', paddingTop: '20px' }}>
            <h3>Share Trip</h3>

            <form onSubmit={handleInviteUser} style={{ display: 'flex', gap: '10px', marginTop: '10px', flexWrap: 'wrap' }}>
              <input
                type="email"
                placeholder="User Email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                required
                style={{ flex: 1, padding: '8px', borderRadius: '4px', border: '1px solid #ddd', minWidth: '180px' }}
              />
              <select
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value)}
                style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
              >
                <option value="viewer">Viewer</option>
                <option value="editor">Editor</option>
              </select>
              <button
                type="submit"
                disabled={isInviting}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#2196F3',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: isInviting ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                {isInviting ? 'Sharing...' : <><FaUserPlus /> Invite</>}
              </button>
            </form>

            {inviteMessage && <p style={{ color: 'green', marginTop: '8px', fontSize: '0.9rem' }}>{inviteMessage}</p>}
            {inviteError && <p style={{ color: 'red', marginTop: '8px', fontSize: '0.9rem' }}>{inviteError}</p>}

            <div style={{ marginTop: '16px' }}>
              <button
                onClick={handleToggleSharedUsers}
                style={{
                  background: 'none',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  padding: '8px 14px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '0.9rem',
                  color: '#555'
                }}
              >
                <FaUsers /> {showSharedUsers ? 'Hide' : 'View'} Shared Users
                {trip.sharedWith && trip.sharedWith.length > 0 && (
                  <span style={{
                    backgroundColor: '#e3f2fd',
                    color: '#1976d2',
                    borderRadius: '10px',
                    padding: '1px 8px',
                    fontSize: '0.8rem',
                    marginLeft: '4px'
                  }}>
                    {trip.sharedWith.length}
                  </span>
                )}
              </button>

              {showSharedUsers && (
                <div style={{ marginTop: '12px' }}>
                  {loadingUsers ? (
                    <p style={{ color: '#666', fontSize: '0.9rem' }}>Loading users...</p>
                  ) : usersError ? (
                    <p style={{ color: 'red', fontSize: '0.9rem' }}>{usersError}</p>
                  ) : sharedUsers.length === 0 ? (
                    <p style={{ color: '#666', fontSize: '0.9rem' }}>No users to display.</p>
                  ) : (
                    <div style={{ border: '1px solid #eee', borderRadius: '6px', overflow: 'hidden' }}>
                      {sharedUsers.map((user) => (
                        <div
                          key={user.uid}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '10px 12px',
                            borderBottom: '1px solid #f0f0f0',
                            gap: '8px'
                          }}
                        >
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 500, fontSize: '0.9rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {user.displayName}
                            </div>
                            <div style={{ color: '#888', fontSize: '0.8rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {user.email}
                            </div>
                          </div>
                          <span style={{
                            padding: '2px 8px',
                            borderRadius: '10px',
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            flexShrink: 0,
                            backgroundColor: user.role === 'owner' ? '#e8f5e9' : user.role === 'editor' ? '#fff3e0' : '#f3e5f5',
                            color: user.role === 'owner' ? '#2e7d32' : user.role === 'editor' ? '#e65100' : '#7b1fa2'
                          }}>
                            {user.role}
                          </span>
                          {isOwner && user.role !== 'owner' && (
                            <button
                              onClick={() => handleRemoveUser(user.uid, user.email)}
                              disabled={removingUid === user.uid}
                              title={`Remove ${user.email}`}
                              style={{
                                background: 'none',
                                border: 'none',
                                color: removingUid === user.uid ? '#ccc' : '#dc3545',
                                cursor: removingUid === user.uid ? 'not-allowed' : 'pointer',
                                padding: '4px',
                                flexShrink: 0
                              }}
                            >
                              <FaUserMinus />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {isOwner && (
          <div className="danger-zone" style={{ marginTop: '40px', borderTop: '1px solid #eee', paddingTop: '20px' }}>
            <button
              onClick={() => {
                if (window.confirm('Are you sure you want to delete this trip?')) {
                  onDelete(trip.id);
                  onRequestClose();
                }
              }}
              className="delete-trip-button"
              style={{
                background: 'none',
                border: 'none',
                color: '#dc3545',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <FaTrash /> Delete Trip
            </button>
          </div>
        )}
      </div>
    </ReactModal>
  );
};

export default TripManagementModal;
